import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  Search,
  Trash2,
  Loader2,
  Image,
  Music,
  Video,
  FileText,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import api from '../../lib/api';
import Modal from './Modal';

function getErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) return err.response?.data?.error?.message || err.message || fallback;
  return fallback;
}

interface FileItem {
  _id: string;
  key: string;
  category: string;
  url: string;
  originalName: string;
  size: number;
}

const CATEGORIES = [
  { value: '', label: 'All', icon: null },
  { value: 'image', label: 'Images', icon: Image },
  { value: 'audio', label: 'Audio', icon: Music },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'document', label: 'Docs', icon: FileText },
] as const;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function categoryIcon(category: string) {
  switch (category) {
    case 'image':
      return <Image size={20} className="text-blue-500" />;
    case 'audio':
      return <Music size={20} className="text-purple-500" />;
    case 'video':
      return <Video size={20} className="text-red-500" />;
    default:
      return <FileText size={20} className="text-gray-500" />;
  }
}

interface FilePickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (file: {
    url: string;
    key: string;
    originalName: string;
    category: string;
    _id: string;
  }) => void;
  accept?: string; // filter to a specific category: 'image' | 'audio' | 'video' | 'document'
}

export default function FilePickerModal({ open, onClose, onSelect, accept }: FilePickerModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pasteTargetRef = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState(accept || '');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['files', category, search, page],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit: 18 };
      if (category) params.category = category;
      if (search) params.search = search;
      const res = await api.get('/files', { params });
      return res.data;
    },
    enabled: open,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/files/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('File deleted');
    },
    onError: () => toast.error('Failed to delete file'),
  });

  useEffect(() => {
    if (open && accept === 'image') {
      pasteTargetRef.current?.focus();
    }
  }, [open, accept]);

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: res } = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('File uploaded');
      onSelect(res.data);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (accept !== 'image' || uploading) return;

    const imageItem = Array.from(e.clipboardData.items).find((item) =>
      item.type.startsWith('image/'),
    );
    if (!imageItem) return;

    const blob = imageItem.getAsFile();
    if (!blob) return;

    e.preventDefault();

    const extension = blob.type.split('/')[1] || 'png';
    const file = new File([blob], `pasted-image-${Date.now()}.${extension}`, {
      type: blob.type || 'image/png',
    });
    await uploadFile(file);
  };

  const files = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <Modal open={open} onClose={onClose} title="File Manager" wide>
      <div
        ref={pasteTargetRef}
        tabIndex={accept === 'image' ? 0 : -1}
        onPaste={handlePaste}
        className="space-y-4 outline-none"
      >
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input-field pl-9"
            />
          </div>

          {/* Category filter tabs */}
          {!accept && (
            <div className="flex gap-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => {
                    setCategory(cat.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 ${
                    category === cat.value
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat.icon && <cat.icon size={12} />}
                  {cat.label}
                </button>
              ))}
            </div>
          )}

          {/* Upload button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-primary text-xs flex items-center gap-1.5"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
        </div>

        {accept === 'image' && (
          <div className="rounded-lg border border-dashed border-primary-200 bg-primary-50/50 px-4 py-3 text-sm text-primary-700">
            Paste an image here with `Ctrl/Cmd + V` to upload it instantly.
          </div>
        )}

        {/* File grid */}
        <div className="min-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-[300px]">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
              <FileText size={40} className="mb-2" />
              <p>No files found</p>
              <p className="text-xs mt-1">Upload a file to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {files.map((file: FileItem) => (
                <div
                  key={file._id}
                  className="group relative border border-gray-200 rounded-lg p-2 hover:border-primary-400 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => {
                    onSelect(file);
                    onClose();
                  }}
                >
                  {/* Preview */}
                  <div className="aspect-square bg-gray-50 rounded flex items-center justify-center mb-2 overflow-hidden">
                    {file.category === 'image' ? (
                      <img
                        src={file.url}
                        alt={file.originalName}
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      categoryIcon(file.category)
                    )}
                  </div>

                  {/* Name */}
                  <p
                    className="text-xs text-gray-700 truncate font-medium"
                    title={file.originalName}
                  >
                    {file.originalName}
                  </p>
                  <p className="text-[10px] text-gray-400">{formatSize(file.size)}</p>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-primary-600/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white rounded-full p-1 shadow">
                      <Check size={16} className="text-primary-600" />
                    </div>
                  </div>

                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(file._id);
                    }}
                    className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-danger-500"
                    title="Delete file"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              {pagination.total} files · Page {pagination.page}/{pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.totalPages}
                className="btn-secondary text-xs"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
